import type { ReactNode } from "react";

/**
 * The layout of a settings form in the desktop shell.
 *
 * A `FormSection` is a titled block of rows. A `FormRow` puts the label and its hint in a left
 * column and the control on the right at desktop width, and stacks them on a phone, which is the
 * layout that lets an owner scan a page of settings by reading the left column alone.
 *
 * Only layout lives here. The controls inside a row are the existing `Field`, `TextAreaField`,
 * `ChipRow` and `ToggleRow`, which keep their own labels for the phone screens; a row that wraps a
 * `Field` passes no `htmlFor` and the field's own label stays the accessible name.
 */
export function FormSection({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: ReactNode;
}) {
  return (
    <section className="rounded-card border border-line bg-paper px-5 shadow-card">
      <div className="border-b border-line py-4">
        <h2 className="text-[17px]">{title}</h2>
        {description ? <p className="mt-1 text-[13px] text-ink-2">{description}</p> : null}
      </div>
      <div className="divide-y divide-line">{children}</div>
    </section>
  );
}

export function FormRow({
  label,
  hint,
  htmlFor,
  children,
}: {
  label: string;
  hint?: string;
  /** The control's id, when the row's label should be its accessible name. */
  htmlFor?: string;
  children: ReactNode;
}) {
  const heading = (
    <>
      <span className="block text-[14px] font-semibold text-ink">{label}</span>
      {hint ? <span className="mt-0.5 block text-[13px] text-ink-2">{hint}</span> : null}
    </>
  );

  return (
    <div className="grid gap-2 py-4 lg:grid-cols-[240px_minmax(0,1fr)] lg:gap-8">
      {htmlFor ? (
        <label htmlFor={htmlFor} className="lg:pt-2.5">
          {heading}
        </label>
      ) : (
        <div className="lg:pt-2.5">{heading}</div>
      )}
      <div className="min-w-0">{children}</div>
    </div>
  );
}
